/**
 * @file Advertisers service (typed CRUD + helpers).
 * @description Encapsulates advertiser API calls, validation, formatting, and list statistics.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import { ApiService } from './apiService';
import { API_ENDPOINTS } from '../constants/api';
import { validateMobilePhone } from '../utils/phoneValidation';
import { parseCount } from '../utils/formatNumbers';
import type {
  Advertiser,
  AdvertiserFormData,
  AdvertisersApiResponse,
  AdvertisersListResult,
  AdvertisersPagination,
  AdvertiserStats,
} from '../types/domain/advertiser';

export type {
  Advertiser,
  AdvertiserFormData,
  AdvertisersApiResponse,
  AdvertisersListResult,
  AdvertisersPagination,
  AdvertiserStats,
} from '../types/domain/advertiser';

function normalizeAdvertisersList(raw: unknown, fallbackPerPage: number): AdvertisersListResult {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};

  const pagSource =
    root['pagination'] ??
    (root['data'] && typeof root['data'] === 'object' && !Array.isArray(root['data'])
      ? (root['data'] as Record<string, unknown>)['pagination']
      : undefined);

  const pag =
    pagSource && typeof pagSource === 'object'
      ? (pagSource as Record<string, unknown>)
      : {};

  const page = Number(pag['page'] ?? 1) || 1;
  const per_page =
    Number(pag['per_page'] ?? fallbackPerPage) || fallbackPerPage;
  const total = Number(pag['total'] ?? 0) || 0;
  let total_page = Number(pag['total_page'] ?? pag['total_pages'] ?? 0) || 0;
  if (total_page === 0 && total > 0 && per_page > 0) {
    total_page = Math.ceil(total / per_page);
  }

  let rows: Advertiser[] = [];
  const data = root['data'];
  if (Array.isArray(data)) {
    rows = data as Advertiser[];
  } else if (data && typeof data === 'object') {
    const inner = data as Record<string, unknown>;
    if (Array.isArray(inner['data'])) rows = inner['data'] as Advertiser[];
    else if (Array.isArray(inner['advertisers'])) rows = inner['advertisers'] as Advertiser[];
  }

  return {
    advertisers: rows,
    pagination: { page, per_page, total, total_page: Math.max(1, total_page || 1) },
  };
}

/** Parse GET /advertisers/list for select dropdowns. */
function normalizeAdvertisersSelectList(raw: unknown): Advertiser[] {
  if (Array.isArray(raw)) {
    return raw as Advertiser[];
  }
  if (!raw || typeof raw !== 'object') {
    return [];
  }
  const root = raw as Record<string, unknown>;
  const data = root['data'];
  if (Array.isArray(data)) {
    return data as Advertiser[];
  }
  if (data && typeof data === 'object') {
    const inner = data as Record<string, unknown>;
    if (Array.isArray(inner['advertisers'])) return inner['advertisers'] as Advertiser[];
    if (Array.isArray(inner['data'])) return inner['data'] as Advertiser[];
    if (Array.isArray(inner['list'])) return inner['list'] as Advertiser[];
  }
  if (Array.isArray(root['advertisers'])) return root['advertisers'] as Advertiser[];
  if (Array.isArray(root['list'])) return root['list'] as Advertiser[];
  return [];
}

/**
 * API requires a valid URL — prepend https:// when user enters a bare domain (e.g. ahy.com).
 */
function normalizeWebsite(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function validateWebsite(value: string): { valid: boolean; message: string | null; normalized: string } {
  const normalized = normalizeWebsite(value);
  if (!normalized) {
    return { valid: true, message: null, normalized: '' };
  }

  try {
    const url = new URL(normalized);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      throw new Error('invalid protocol');
    }
    if (!url.hostname.includes('.')) {
      throw new Error('invalid host');
    }
    return { valid: true, message: null, normalized };
  } catch {
    return {
      valid: false,
      message: 'Enter a valid website URL (e.g. example.com or https://example.com)',
      normalized: '',
    };
  }
}

function buildAdvertiserApiPayload(
  formData: AdvertiserFormData,
  normalizedPhone: string,
  normalizedWebsite: string,
): AdvertiserFormData {
  return {
    name: formData.name.trim(),
    email: formData.email.trim(),
    phone: normalizedPhone || formData.phone.trim(),
    website: normalizedWebsite,
  };
}

/**
 * Advertisers Service
 * Handles all advertiser-related API operations
 */
export class AdvertisersService {
  /**
   * Fetch advertisers with server pagination (table listing).
   */
  static async listAdvertisers(
    page: number = 1,
    perPage: number = 10,
  ): Promise<AdvertisersListResult> {
    try {
      const query = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
        _: String(Date.now()),
      });
      const response = await ApiService.makeRequest<AdvertisersApiResponse | Record<string, unknown>>(
        `${API_ENDPOINTS.ADVERTISERS}?${query.toString()}`,
        'GET',
      );
      return normalizeAdvertisersList(response, perPage);
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to fetch advertisers');
    }
  }

  /**
   * Fetch advertisers for select dropdowns (GET /advertisers/list).
   * Called per page on mount — no shared cache across routes.
   */
  static async getAdvertisers(): Promise<Advertiser[]> {
    try {
      const response = await ApiService.makeRequest<AdvertisersApiResponse | Record<string, unknown>>(
        API_ENDPOINTS.ADVERTISERS_LIST,
        'GET',
      );
      return normalizeAdvertisersSelectList(response.data ?? response);
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to fetch advertisers list');
    }
  }

  /**
   * Create a new advertiser
   */
  static async createAdvertiser(formData: AdvertiserFormData): Promise<void> {
    try {
      const body = buildAdvertiserApiPayload(formData, formData.phone, formData.website);
      const formDataToSend = new FormData();
      formDataToSend.append('name', body.name);
      formDataToSend.append('email', body.email);
      formDataToSend.append('phone', body.phone);
      formDataToSend.append('website', body.website);

      await ApiService.makeRequest<{ message?: string }>(
        API_ENDPOINTS.ADVERTISERS,
        'POST',
        formDataToSend,
      );
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Failed to create advertiser');
    }
  }

  /**
   * Update an existing advertiser
   */
  static async updateAdvertiser(id: number, formData: AdvertiserFormData): Promise<void> {
    try {
      const advertiserData = buildAdvertiserApiPayload(formData, formData.phone, formData.website);

      await ApiService.makeRequest<{ message?: string }>(
        `${API_ENDPOINTS.ADVERTISERS}/${id}`,
        'PUT',
        advertiserData,
      );
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Failed to update advertiser');
    }
  }

  /**
   * Delete an advertiser
   */
  static async deleteAdvertiser(id: number): Promise<{ message?: string }> {
    try {
      const response = await ApiService.makeRequest<{ message?: string }>(
        `${API_ENDPOINTS.ADVERTISERS}/${id}`,
        'DELETE',
        new URLSearchParams(),
      );
      return response;
    } catch (error) {
      // Check for 409 status code
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status: number }).status;
        
        if (status === 409) {
          // 409 Conflict - Advertiser has active campaigns
        }
      }
      
      // Extract error message from API response
      if (error && typeof error === 'object' && 'message' in error) {
        throw new Error((error as { message: string }).message);
      }
      throw error instanceof Error ? error : new Error('Failed to delete advertiser');
    }
  }

  /**
   * Toggle advertiser status (active/inactive)
   */
  static async toggleAdvertiserStatus(id: number, status: '0' | '1'): Promise<void> {
    try {
      await ApiService.makeRequest<{ message?: string }>(
        `${API_ENDPOINTS.ADVERTISERS}/status/${id}`,
        'POST',
        {status: status},
      );
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to toggle advertiser status');
    }
  }

  /**
   * Get advertiser statistics
   */
  static async getAdvertiserStats(): Promise<AdvertiserStats> {
    try {
      const { advertisers, pagination } = await this.listAdvertisers(1, 500);

      return {
        totalAdvertisers: pagination.total || advertisers.length,
        totalCampaigns: advertisers.reduce(
          (sum, advertiser) => sum + parseCount(advertiser.total_campaigns),
          0,
        ),
        totalLeads: advertisers.reduce(
          (sum, advertiser) => sum + parseCount(advertiser.total_leads),
          0,
        ),
      };
    } catch {
      return {
        totalAdvertisers: 0,
        totalCampaigns: 0,
        totalLeads: 0,
      };
    }
  }

  /**
   * Validate advertiser form data
   */
  static validateAdvertiserForm(formData: AdvertiserFormData): {
    isValid: boolean;
    errors: string[];
    fieldErrors: Partial<Record<keyof AdvertiserFormData, string>>;
    normalizedPhone: string;
    normalizedWebsite: string;
  } {
    const errors: string[] = [];
    const fieldErrors: Partial<Record<keyof AdvertiserFormData, string>> = {};

    if (!formData.name.trim()) {
      const msg = 'Company name is required';
      errors.push(msg);
      fieldErrors.name = msg;
    }

    if (!formData.email.trim()) {
      const msg = 'Email is required';
      errors.push(msg);
      fieldErrors.email = msg;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      const msg = 'Please enter a valid email address';
      errors.push(msg);
      fieldErrors.email = msg;
    }

    const phoneCheck = validateMobilePhone(formData.phone);
    if (!phoneCheck.valid) {
      const msg = phoneCheck.message ?? 'Please enter a valid phone number';
      errors.push(msg);
      fieldErrors.phone = msg;
    }

    const websiteCheck = validateWebsite(formData.website);
    if (!websiteCheck.valid) {
      const msg = websiteCheck.message ?? 'Please enter a valid website';
      errors.push(msg);
      fieldErrors.website = msg;
    }

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors,
      normalizedPhone: phoneCheck.normalized,
      normalizedWebsite: websiteCheck.normalized,
    };
  }

  /**
   * Format date for display
   */
  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * Format website URL for display
   */
  static formatWebsiteUrl(url: string): string {
    return url.replace(/^https?:\/\//, '');
  }

  /**
   * Check if advertiser can be deleted (no active campaigns)
   */
  static canDeleteAdvertiser(advertiser: Advertiser): boolean {
    return (advertiser.total_campaigns || 0) === 0;
  }
}
